import React, { useState, useEffect, useRef } from 'react';
import { orderAPI } from '../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Printer, ArrowLeft, Loader2, MessageCircle } from 'lucide-react';

export const InvoiceView = ({ orderId, onBack }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    fetchInvoice();
  }, [orderId]);

  const fetchInvoice = async () => {
    try {
      const res = await orderAPI.getInvoice(orderId);
      setInvoice(res.data);
    } catch (error) {
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const win = window.open('', '_blank');
    const doc = win.document;

    doc.title = `Invoice ${invoice.invoice_number}`;

    const style = doc.createElement('style');
    style.textContent = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'IBM Plex Sans', Arial, sans-serif; padding: 32px; color: #1E2420; }
      .invoice-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 2px solid #2B5339; }
      .company-name { font-size: 24px; font-weight: 700; color: #2B5339; margin-bottom: 4px; }
      .company-details { font-size: 12px; color: #5C6B61; line-height: 1.5; }
      .invoice-title { text-align: right; }
      .invoice-title h2 { font-size: 28px; font-weight: 700; color: #2B5339; }
      .invoice-title p { font-size: 12px; color: #5C6B61; }
      .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 32px; }
      .info-box { padding: 16px; background: #FBF9F6; border-radius: 6px; }
      .info-box h4 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.1em; color: #5C6B61; margin-bottom: 8px; font-weight: 600; }
      .info-box p { font-size: 14px; line-height: 1.6; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
      th { background: #2B5339; color: white; padding: 12px 16px; text-align: left; font-size: 13px; font-weight: 600; }
      td { padding: 12px 16px; border-bottom: 1px solid #E2DFD8; font-size: 14px; }
      tr:last-child td { border-bottom: none; }
      .total-row { background: #FBF9F6; }
      .total-row td { font-weight: 700; font-size: 16px; color: #2B5339; }
      .footer { margin-top: 48px; padding-top: 24px; border-top: 1px solid #E2DFD8; text-align: center; font-size: 12px; color: #5C6B61; }
      .dispatch-info { background: #FBF9F6; padding: 16px; border-radius: 6px; margin-bottom: 24px; }
      .dispatch-info h4 { color: #C05C35; font-weight: 600; margin-bottom: 8px; }
      .dispatch-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .dispatch-grid p { font-size: 13px; }
      .dispatch-grid .label { color: #5C6B61; font-size: 11px; }
      .dispatch-grid .value { font-weight: 600; }
      @media print { body { padding: 16px; } }
    `;
    doc.head.appendChild(style);

    // Import a deep clone of the already-rendered invoice node into the print window (no document.write).
    doc.body.appendChild(doc.importNode(printContent, true));

    doc.close();
    win.focus();
    win.print();
  };

  const normalizeWhatsAppNumber = (raw) => {
    if (!raw) return '';
    const digits = String(raw).replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('91') && digits.length >= 12) return digits;
    if (digits.length === 10) return `91${digits}`;
    return digits;
  };

  const handleWhatsAppShare = () => {
    const waNumber = normalizeWhatsAppNumber(invoice.dealer_phone);
    if (!waNumber) {
      toast.error('Dealer mobile number not available');
      return;
    }

    const fmtDate = (d) => {
      if (!d) return '';
      const dt = new Date(d.includes && d.includes('T') ? d : d + 'T00:00:00');
      return dt.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const lines = [];
    lines.push('*Vikas Industries*');
    lines.push('Palsana, Rajasthan');
    lines.push('');
    lines.push(`Namaste ${invoice.dealer_name},`);
    lines.push('');
    lines.push(`Your order *${invoice.invoice_number}* has been *${invoice.status === 'dispatched' ? 'dispatched' : invoice.status}*.`);
    lines.push('');

    if (invoice.delivery_date) {
      lines.push(`*Delivery:* ${fmtDate(invoice.delivery_date)}${invoice.delivery_time ? ' at ' + invoice.delivery_time : ''}`);
    }
    if (invoice.vehicle_number) {
      lines.push(`*Vehicle:* ${invoice.vehicle_number}`);
    }
    if (invoice.driver_mobile) {
      lines.push(`*Driver:* ${invoice.driver_mobile}`);
    }
    if (invoice.vehicle_number || invoice.delivery_date) lines.push('');

    lines.push('*Items:*');
    (invoice.items || []).forEach((it, i) => {
      lines.push(`${i + 1}. ${it.product_name} — ${it.quantity} x \u20B9${it.price} = \u20B9${it.total}`);
    });
    lines.push('');

    const net = (invoice.net_amount ?? invoice.total_amount) || 0;
    if (invoice.discount_percent) {
      lines.push(`Gross: \u20B9${invoice.total_amount}`);
      lines.push(`Discount (${invoice.discount_percent}%): -\u20B9${(invoice.discount_amount || 0).toFixed(2)}`);
    }
    lines.push(`*Net Payable: \u20B9${Number(net).toFixed(2)}*`);
    lines.push('');
    lines.push('Thank you for your business!');
    lines.push('— Vikas Industries');

    const text = encodeURIComponent(lines.join('\n'));
    const url = `https://wa.me/${waNumber}?text=${text}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#2B5339' }} />
      </div>
    );
  }

  if (!invoice) {
    return <p>Invoice not found</p>;
  }

  return (
    <div data-testid="invoice-view">
      <div className="flex flex-wrap items-center gap-2 md:gap-4 mb-4 md:mb-6">
        <Button variant="outline" onClick={onBack} data-testid="invoice-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button onClick={handlePrint} style={{ background: '#2B5339' }} data-testid="print-invoice-btn">
          <Printer className="w-4 h-4 mr-2" /> Print Invoice
        </Button>
        <Button onClick={handleWhatsAppShare} style={{ background: '#25D366', color: 'white' }} data-testid="whatsapp-share-btn">
          <MessageCircle className="w-4 h-4 mr-2" /> Share on WhatsApp
        </Button>
      </div>

      <Card className="p-4 md:p-8 bg-white border" style={{ borderColor: '#E2DFD8' }}>
        <div ref={printRef}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-6 md:mb-8 pb-4 md:pb-6" style={{ borderBottom: '2px solid #2B5339' }}>
            <div className="flex items-start gap-3 md:gap-4 min-w-0">
              <img src="/logo.jpg" alt="Vikas Industries" className="w-24 md:w-60 rounded object-contain flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="text-lg md:text-2xl font-bold" style={{ color: '#2B5339' }}>Vikas Industries</h2>
              <p className="text-xs md:text-sm mt-1" style={{ color: '#5C6B61' }}>E 302-303, RIICO, Palsana-332402 (Rajasthan)</p>
              <p className="text-xs md:text-sm" style={{ color: '#5C6B61' }}>Phone: 9414037300, 9587367041</p>
              <p className="text-xs md:text-sm" style={{ color: '#5C6B61' }}>GSTIN: 08XXXXXXXXXXXZX</p>
              </div>
            </div>
            <div className="text-left md:text-right">
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#2B5339' }}>INVOICE</h2>
              <p className="text-sm mt-1" style={{ color: '#5C6B61' }} data-testid="invoice-number">{invoice.invoice_number}</p>
              <p className="text-sm" style={{ color: '#5C6B61' }}>
                Date: {new Date(invoice.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </p>
            </div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="p-4 rounded" style={{ background: '#FBF9F6' }}>
              <h4 className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: '#5C6B61' }}>Bill To</h4>
              <p className="font-medium" style={{ color: '#1E2420' }} data-testid="invoice-dealer-name">{invoice.dealer_name}</p>
              <p className="text-sm" style={{ color: '#5C6B61' }}>{invoice.dealer_phone}</p>
            </div>
            <div className="p-4 rounded" style={{ background: '#FBF9F6' }}>
              <h4 className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: '#5C6B61' }}>Order Details</h4>
              <p className="text-sm" style={{ color: '#1E2420' }}>Order ID: #{invoice.order_id.slice(0, 8)}</p>
              <p className="text-sm" style={{ color: '#1E2420' }}>
                Order Date: {new Date(invoice.order_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </p>
              {invoice.delivery_date && (
                <p className="text-sm" style={{ color: '#1E2420' }}>
                  Delivery: {new Date(invoice.delivery_date.includes('T') ? invoice.delivery_date : invoice.delivery_date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })} at {invoice.delivery_time}
                </p>
              )}
            </div>
          </div>

          {/* Dispatch Info */}
          {invoice.vehicle_number && (
            <div className="p-4 rounded mb-8" style={{ background: '#FBF9F6' }}>
              <h4 className="font-semibold mb-2" style={{ color: '#C05C35' }}>Dispatch Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs" style={{ color: '#5C6B61' }}>Vehicle Number</p>
                  <p className="font-medium" data-testid="invoice-vehicle-number">{invoice.vehicle_number}</p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: '#5C6B61' }}>Driver Mobile</p>
                  <p className="font-medium" data-testid="invoice-driver-mobile">{invoice.driver_mobile}</p>
                </div>
              </div>
            </div>
          )}

          {/* Items Table */}
          <div className="overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0">
          <table className="w-full mb-6 min-w-[500px]">
            <thead>
              <tr style={{ background: '#2B5339' }}>
                <th className="text-left p-3 text-white text-sm font-semibold rounded-tl">#</th>
                <th className="text-left p-3 text-white text-sm font-semibold">Product</th>
                <th className="text-right p-3 text-white text-sm font-semibold">Qty</th>
                <th className="text-right p-3 text-white text-sm font-semibold">Price</th>
                <th className="text-right p-3 text-white text-sm font-semibold rounded-tr">Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #E2DFD8' }}>
                  <td className="p-3 text-sm">{idx + 1}</td>
                  <td className="p-3 text-sm font-medium">{item.product_name}</td>
                  <td className="p-3 text-sm text-right">{item.quantity}</td>
                  <td className="p-3 text-sm text-right">{'\u20B9'}{item.price}</td>
                  <td className="p-3 text-sm text-right font-medium">{'\u20B9'}{item.total}</td>
                </tr>
              ))}
              <tr style={{ background: '#FBF9F6' }}>
                <td colSpan="4" className="p-3 text-right font-medium text-sm" style={{ color: '#5C6B61' }}>Gross Total</td>
                <td className="p-3 text-right font-medium text-sm">{'\u20B9'}{invoice.total_amount}</td>
              </tr>
              <tr>
                <td colSpan="4" className="p-3 text-right text-sm" style={{ color: '#C05C35' }}>Dealer Discount ({invoice.discount_percent || 0}%)</td>
                <td className="p-3 text-right text-sm" style={{ color: '#C05C35' }}>- {'\u20B9'}{(invoice.discount_amount || 0).toFixed(2)}</td>
              </tr>
              <tr style={{ background: '#2B5339' }}>
                <td colSpan="4" className="p-3 text-right font-bold text-lg text-white">Net Payable</td>
                <td className="p-3 text-right font-bold text-lg text-white" data-testid="invoice-total">{'\u20B9'}{(invoice.net_amount || invoice.total_amount).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-6 text-center" style={{ borderTop: '1px solid #E2DFD8' }}>
            <p className="text-sm" style={{ color: '#5C6B61' }}>Thank you for your business!</p>
            <p className="text-xs mt-1" style={{ color: '#5C6B61' }}>Vikas Industries | E 302-303, RIICO, Palsana-332402 (Rajasthan)</p>
          </div>
        </div>
      </Card>
    </div>
  );
};
